/**
 * "View as role" — an admin-only, client-side preview of the UI as a lower
 * role. Lets an administrator see what a Developer or User would see (which
 * nav items, which pages) without a second account.
 *
 * UI ONLY. This never changes what the caller can actually do — the backend
 * re-checks the real role on every request (CLAUDE.md §5.3). A non-admin can
 * never preview; their real roles always win.
 *
 * Context lives in its own file (no component export) so the fast-refresh
 * only-export-components rule stays happy — mirrors AuthConfigContext.
 */

import { createContext, useContext } from "react";

export interface RolePreviewValue {
  /** Lowercased role being previewed (`user` | `developer` | `administrator`),
   *  or null when the admin is viewing as themselves. */
  previewRole: string | null;
  setPreviewRole: (role: string | null) => void;
}

export const RolePreviewContext = createContext<RolePreviewValue>({
  previewRole: null,
  setPreviewRole: () => {},
});

// Role inheritance, mirroring the backend policy ladder (administrator ⊇
// developer ⊇ user). Previewing "developer" should still grant user-level
// surfaces, exactly as a real developer inherits them.
const ROLE_LADDER: Record<string, string[]> = {
  user: ["user"],
  developer: ["developer", "user"],
  administrator: ["administrator", "developer", "user"],
};

export function useRolePreview(): RolePreviewValue {
  return useContext(RolePreviewContext);
}

/**
 * The roles the UI should gate on. When an administrator has selected a
 * preview role, returns that role expanded for inheritance; otherwise the
 * real roles unchanged. Only real administrators may preview.
 */
export function useEffectiveRoles(realRoles: string[]): string[] {
  const { previewRole } = useContext(RolePreviewContext);
  if (!previewRole) return realRoles;
  if (!realRoles.includes("administrator")) return realRoles;
  return ROLE_LADDER[previewRole] ?? realRoles;
}
